import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, Subject, switchMap, tap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import {
  BoxSizeResponse,
  BoxSizeService,
} from '@nebalkon-workspace/storage-api';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import { bindStaffPageTitleCount } from './staff-page-title-count.util';
import { bindStaffPageHeaderAction } from './staff-page-header-action.util';

@Component({
  selector: 'lib-staff-box-types-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, CabinetButtonComponent, RouterLink],
  templateUrl: './staff-box-types-list.component.html',
  styleUrl: './staff-box-types-list.component.scss',
})
export class StaffBoxTypesListComponent implements OnInit {
  readonly embeddedInCabinetPage = input(false);

  readonly #cabinetPaths = inject(CABINET_PATHS);
  protected readonly staffCabinetPath = this.#cabinetPaths.staff;
  readonly #boxSizeService = inject(BoxSizeService);
  readonly #router = inject(Router);

  protected readonly loading = signal(true);
  protected readonly initialized = signal(false);
  protected readonly headerAddActionTpl = viewChild<TemplateRef<unknown>>('headerAddAction');

  private readonly refreshSubject = new Subject<void>();

  protected readonly boxTypes = toSignal(
    this.refreshSubject.pipe(
      tap(() => this.loading.set(true)),
      switchMap(() =>
        this.#boxSizeService.getAdminBoxSizes().pipe(
          tap(() => {
            this.loading.set(false);
            this.initialized.set(true);
          }),
          catchError(() => {
            this.loading.set(false);
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  protected readonly displayCount = computed(() => {
    if (!this.initialized()) {
      return null;
    }
    const data = this.boxTypes()?.data;
    if (!data) {
      return null;
    }
    return data.filter((boxType) => boxType.is_active).length;
  });

  constructor() {
    bindStaffPageTitleCount(this.embeddedInCabinetPage, this.displayCount);
    bindStaffPageHeaderAction(
      this.embeddedInCabinetPage,
      this.headerAddActionTpl,
      this.initialized,
    );
  }

  public ngOnInit(): void {

    this.refreshSubject.next();
  }

  protected onAdd(): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'box-types', 'create']);
  }

  protected formatDimensions(boxType: BoxSizeResponse): string {
    return `${boxType.side1_mm}×${boxType.side2_mm}×${boxType.height_mm} мм`;
  }
}
